// Pre-publish "what's about to change" modal. Backed by /api/admin/scenes/:id/diff.
// Extracted from SceneBuilderPage so it's testable in isolation without dragging
// the SAM worker / scene-data into the test environment.
import { diffIsEmpty, type DiffPayload } from "./diff-utils";
import { useTheme } from "@/lib/theme";

export type { DiffPayload };

export function DiffOverlay({
  diff,
  onCancel,
  onConfirm,
}: {
  diff: DiffPayload;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const noChanges = diffIsEmpty(diff);
  const { theme } = useTheme();
  const onAccent = theme === "light" ? "#000" : "#fff";
  return (
    <div
      onClick={onCancel}
      role="dialog"
      aria-label="Publish to live"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg, #fff)",
          color: "var(--text)",
          borderRadius: 12,
          padding: "20px 24px",
          maxWidth: 560,
          width: "100%",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Publish to live</h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: "1.4rem",
              color: "var(--text-muted)",
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {noChanges ? (
          <p style={{ margin: 0, color: "var(--text-muted)" }}>
            Draft is identical to live. Publishing will be a no-op.
          </p>
        ) : (
          <>
            <p style={{ margin: "0 0 12px 0", fontSize: "0.9rem", color: "var(--text-muted)" }}>
              These changes will become visible to users:
            </p>
            <DiffSection
              title="Items"
              added={diff.items.added.map((i) => i.english)}
              removed={diff.items.removed.map((i) => i.english)}
              changed={diff.items.changed.map(
                (i) =>
                  `${i.english}${i.polygonChanged ? " (polygon)" : ""}${i.englishChanged ? " (label)" : ""}`
              )}
            />
            <DiffSection
              title="Translations"
              added={diff.translations.added.map((t) => `${t.itemId} · ${t.lang}`)}
              removed={diff.translations.removed.map((t) => `${t.itemId} · ${t.lang}`)}
              changed={diff.translations.changed.map((t) => `${t.itemId} · ${t.lang}`)}
            />
          </>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="secondary-button"
            onClick={onCancel}
            style={{ padding: "6px 14px", fontSize: "0.88rem" }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={onConfirm}
            disabled={noChanges}
            style={{ padding: "6px 14px", fontSize: "0.88rem", background: "var(--accent)", color: onAccent }}
          >
            {noChanges ? "Nothing to publish" : "Publish to live"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DiffSection({
  title,
  added,
  removed,
  changed,
}: {
  title: string;
  added: string[];
  removed: string[];
  changed: string[];
}) {
  if (added.length + removed.length + changed.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <h3 style={{ margin: "0 0 6px 0", fontSize: "0.92rem" }}>{title}</h3>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: "0.86rem" }}>
        {added.map((a) => (
          <li key={`a-${a}`} style={{ padding: "3px 8px", color: "#3cb44b" }}>
            + {a}
          </li>
        ))}
        {removed.map((r) => (
          <li key={`r-${r}`} style={{ padding: "3px 8px", color: "var(--error, #e53e3e)" }}>
            − {r}
          </li>
        ))}
        {changed.map((c) => (
          <li key={`c-${c}`} style={{ padding: "3px 8px", color: "#f58231" }}>
            ~ {c}
          </li>
        ))}
      </ul>
    </div>
  );
}
